import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, Bug, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { captureError } from "@/lib/sentry";
import { addClientErrorToBuffer, sanitizeBugReportText } from "@/lib/bug-report-context";
import { useBugReportDialog } from "./BugReportProvider";
import { useAuth } from "@/contexts/AuthContext";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  sentryEventId: string | null;
}

function AppRuntimeErrorFallback({ error, sentryEventId }: { error: Error; sentryEventId: string | null }) {
  const { openReportDialog } = useBugReportDialog();
  const { user } = useAuth();
  const safeMessage = sanitizeBugReportText(error.message || "Unexpected application error");

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="mb-2 flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <CardTitle>Something went wrong</CardTitle>
          </div>
          <CardDescription>
            The app hit an unexpected problem. You can refresh, or send a bug report with safe technical context.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
            <p className="font-mono">{safeMessage}</p>
            {sentryEventId && <p className="mt-2 font-mono">Sentry: {sentryEventId}</p>}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button className="flex-1" onClick={() => window.location.reload()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            {user && (
              <Button
                className="flex-1"
                variant="outline"
                onClick={() =>
                  openReportDialog({
                    reportKind: "runtime_error",
                    priority: "high",
                    area: "runtime",
                    subject: `App error: ${safeMessage}`.slice(0, 140),
                    description:
                      "The app showed an unexpected error screen. Please review the attached technical context and recent sanitized client errors.",
                    expectedBehavior: "The screen should continue working without crashing.",
                    stepsToReproduce: "Open the same page and repeat the last action before this error appeared.",
                    sentryEventId,
                  })
                }
              >
                <Bug className="mr-2 h-4 w-4" />
                Report this problem
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export class AppRuntimeErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, sentryEventId: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, sentryEventId: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const sentryEventId = captureError(error, {
      componentStack: errorInfo.componentStack,
      boundary: "AuthenticatedAppRuntimeErrorBoundary",
    });

    addClientErrorToBuffer({
      message: error.message || "Authenticated app runtime error",
      source: "manual",
      stack: error.stack,
    });

    this.setState({ sentryEventId: sentryEventId || null });
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <AppRuntimeErrorFallback
          error={this.state.error}
          sentryEventId={this.state.sentryEventId}
        />
      );
    }

    return this.props.children;
  }
}
